import {assert, assertDefined} from "@prosay/common"
import {TextInput} from "@prosay/components"
import {translate} from "@prosay/i18n"
import {defaultDataIdFromObject} from "apollo-cache-inmemory"
import React, {useCallback, useEffect, useRef} from "react"
// eslint-disable-next-line sort-imports
import {StyleSheet, TextInput as NativeTextInput, View} from "react-native"
import {Button, IconButton} from "react-native-paper"
import {
    LastMessageFragment,
    LastMessageFragmentDoc,
    MessageInfoFragment,
    useEditTextMessageMutation,
} from "../graphql"
import {Field, Form} from "./Form"

const styles = StyleSheet.create({
    container: {
        // marginBottom: 13, // on web, this needs to be 13.
        borderTopColor: "rgba(0, 0, 0, 0.1)",
        borderTopWidth: 1,

        flexDirection: "row",
        // height: 45,

        left: 0,
        right: 0,
    },
    input: {
        // borderColor: colors.border,
        // borderTopWidth: 1,
    },
    inputContainer: {
        flex: 1,
    },
    sendButton: {
        borderBottomRightRadius: 0,
        borderTopRightRadius: 0,
    },
})

// TODO: Currently, submitting an edit gives the following error: Warning: Can't perform a React state update on an unmounted component. This is a no-op, but it indicates a memory leak in your application. To fix, cancel all subscriptions and asynchronous tasks in a useEffect cleanup function.
export interface MessageEditInputBarProps {
    conversationId: string
    editMessage: MessageInfoFragment | undefined
    setEditMessage: (message: MessageInfoFragment | undefined) => void
}
export const MessageEditInputBar: React.FC<MessageEditInputBarProps> = ({
    conversationId,
    editMessage,
    setEditMessage,
}) => {
    assert(editMessage?.content?.__typename === "TextContent")
    const textInputRef = useRef<NativeTextInput>(null)
    useEffect(() => {
        if (!textInputRef.current) {
            return
        }

        const immediate = setImmediate(() =>
            assertDefined(textInputRef.current).focus(),
        )
        return () => clearImmediate(immediate)
    }, [])

    const mutation = useEditTextMessageMutation({
        onCompleted: () => {
            setEditMessage(undefined)
        },
        optimisticResponse: ({id, textContent}) => ({
            __typename: "Mutation",
            editTextMessage: {
                ...editMessage,
                content: {
                    __typename: "TextContent",
                    content: textContent,
                    id,
                },
                id,
            },
        }),
        update: (cache, {data, errors}) => {
            if (errors || !data?.editTextMessage) {
                return
            }

            const id = defaultDataIdFromObject({
                __typename: "Conversation",
                id: conversationId,
            })
            if (
                !id ||
                cache.readFragment<LastMessageFragment>({
                    fragment: LastMessageFragmentDoc,
                    fragmentName: "LastMessage",
                    id,
                })?.lastMessage?.id !== data.editTextMessage.id
            ) {
                return
            }

            cache.writeFragment<LastMessageFragment>({
                data: {
                    __typename: "Conversation",
                    lastMessage: data.editTextMessage,
                    lastMessageUpdatedAt: data.editTextMessage.createdAt,
                },
                fragment: LastMessageFragmentDoc,
                fragmentName: "LastMessage",
                id,
            })
        },
        variables: editMessage
            ? {
                  id: editMessage.id,
                  textContent:
                      editMessage.content.__typename === "TextContent"
                          ? editMessage.content.content
                          : "",
              }
            : undefined,
    })

    const onSuccess = useCallback(() => setEditMessage(undefined), [
        setEditMessage,
    ])

    return (
        <Form
            mutation={mutation}
            initialValues={{
                id: editMessage.id,
                textContent: editMessage.content.content,
            }}
            onSuccess={onSuccess}>
            {({isSubmitting, isValid, submitForm}) => (
                <View style={styles.container}>
                    <Button onPress={onSuccess}>
                        <IconButton icon="close" />
                    </Button>

                    <Field mutation={mutation} name="textContent">
                        {({field: {error, onBlur, onChangeText, value}}) => (
                            <TextInput
                                ref={ref => ref?.focus?.()}
                                mode="outlined"
                                containerStyle={styles.inputContainer}
                                style={styles.input}
                                blurOnSubmit={false}
                                onSubmitEditing={submitForm}
                                placeholder={translate(
                                    "ui.components.messageInputBar.textPlaceholder",
                                )}
                                {...{
                                    error,
                                    onBlur,
                                    onChangeText,
                                    value: value ?? "",
                                }}
                            />
                        )}
                    </Field>

                    <Button
                        style={styles.sendButton}
                        disabled={!isValid || isSubmitting}
                        onPress={submitForm}>
                        <IconButton icon="send" />
                    </Button>
                </View>
            )}
        </Form>
    )
}
